import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, ButtonGroup } from "reactstrap";
import { useNavigate } from "react-router";
import { Plus } from "react-feather";
import SearchBarComponent from "components/customs/searchbarV2";
import CustomDataTable from "components/customs/table";
import { defaultTableOption, rowPerPage } from "constants/table";
import { hasPermission, showDeleteModal } from "utility/helpers";
import { genericGet } from "services/services.general";
import CommentsModal from "components/customs/comments-modal";
import { AddButton, HeaderText } from "assets/js/Styles";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { ADD_ACTION } from "constants/actions";
import eventColumns from "./eventColumns";
import congressmanEventColumns from "./congressmanEventColumns";

const StyledButtonGroup = styled(ButtonGroup)`
  .view-active {
    font-weight: bold;
    border: none;
    background: ${({ theme }) => theme.colors.blue} !important;
    color: ${({ theme }) => theme.colors.white} !important;
  }

  .btn-secondary {
    background-color: #fff;
    border: none;
    color: ${({ theme }) => theme.colors.blue};

    &:hover {
      background-color: #e9ecef;
    }
  }
`;

const HeaderTextSection = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;
`;

const TableSection = styled.div`
  margin-top: 16px;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const SearchbarWrapper = styled.div`
  width: 50%;
  position: relative;
  justify-self: flex-end;

  input {
    border: 2px solid ${({ theme }) => theme.colors.black60};
    background: white;
    box-shadow: none;
    border-radius: 9px;
    height: clamp(35px, 5vw, 50px);
  }
`;

const StyledAddButton = styled(AddButton)`
  width: 130px;
  background: ${({ theme }) => theme.colors.buttonBlue};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AddButtonText = styled.span`
  padding-left: 8px;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.63px;
`;

const AddAccountSection = styled.div`
  display: flex;
  margin-left: 16px;

  a {
    text-decoration: none;
  }

  @media (max-width: 768px) {
    a {
      width: 100%;
    }
  }
`;

const TableHeaderSection = styled.div`
  margin-top: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TableHeaderRightSection = styled.div`
  display: flex;
  flex-direction: row;
  width: 50%;
  justify-content: flex-end;
`;

const EventsList = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [activeView, setActiveView] = useState("Event");
  const [isModalShown, setIsModalShown] = useState(false);
  const [commentId, setCommentId] = useState(null);
  const [comments, setComments] = useState([]);
  const [searchVal, setSearchValue] = useState("");

  const toggleModal = () => setIsModalShown(!isModalShown);

  const navigate = useNavigate();

  const changeSearchTimeoutID = useRef(null);

  const onChangeSearch = (event) => {
    event.persist(); // This is unnecessary in modern React, you can remove it

    if (changeSearchTimeoutID.current) {
      clearTimeout(changeSearchTimeoutID.current);
    }

    const eventValue = event.target.value;
    setSearchValue(eventValue); // Updating searchValue immediately

    changeSearchTimeoutID.current = setTimeout(() => {
      setTableOption((prev) => ({
        ...prev,
        page: 1,
        searchValue: eventValue.trim(),
      }));
    }, 1000);
  };

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  // eslint-disable-next-line consistent-return
  const handleViewChange = (eventView) => {
    if (activeView !== eventView) {
      setActiveView(eventView);

      setSearchValue("");

      setTableOption({
        ...tableOption,
        isLoading: true,
        searchValue: null,
        sort: {
          field: null,
          direction: null,
        },
      });
    }
  };

  const fetchDataTable = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    genericGet({
      url: activeView === "Event" ? "/events" : "/events/congressman",
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          data: data.rows,
          totalRows: data.count,
          isLoading: false,
        });
      },
    });
  };

  const fetchComments = (id) => {
    genericGet({
      url: `/events/${id}/comments`,
      callback: (data) => {
        setComments(data);
      },
    });
  };

  const handleDeleteAction = (row) => {
    showDeleteModal({
      title: `event ${row?.eventName}`,
      url: `/events/${row.id}`,
      callBack: () => {
        fetchDataTable();
      },
    });
  };

  const fetchCommentsCallback = useCallback((id) => {
    genericGet({
      url: `/events/${id}/comments`,
      callback: (data) => {
        setComments(data);
      },
    });
  }, []);

  const toggleModalCallback = useCallback(() => {
    setIsModalShown((prev) => !prev);
  }, []);

  const EVENT_COLUMNS = eventColumns({
    navigate,
    handleDeleteAction,
    setCommentId,
    fetchComments: fetchCommentsCallback,
    toggleModal: toggleModalCallback,
  });

  const CONGRESSMAN_EVENT_COLUMNS = congressmanEventColumns({ navigate });

  useEffect(() => {
    fetchDataTable();
  }, [
    activeView,
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.direction,
    tableOption.sort.field,
  ]);

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <HeaderTextSection>
            <HeaderText>{"Events"}</HeaderText>
          </HeaderTextSection>

          <TableHeaderSection>
            <StyledButtonGroup>
              <Button
                className={`${activeView === "Event" ? "view-active" : ""}`}
                isDisabled={activeView === "Event"}
                onClick={() => handleViewChange("Event")}
              >
                {"BY EVENT"}
              </Button>
              <Button
                className={`${activeView === "CongCos" ? "view-active" : ""}`}
                isDisabled={activeView === "CongCos"}
                onClick={() => handleViewChange("CongCos")}
              >
                {"BY CONG/COS"}
              </Button>
            </StyledButtonGroup>

            <TableHeaderRightSection>
              <SearchbarWrapper>
                <SearchBarComponent
                  value={searchVal}
                  placeholder="Search Cong/COS..."
                  onChangeSearch={onChangeSearch}
                  isDisabled={tableOption?.isLoading}
                />
              </SearchbarWrapper>

              <AddAccountSection>
                {activeView === "Event"
                  ? activeView === "Event" &&
                    hasPermission("event-create") && (
                      <Link
                        to="/events/add"
                        state={{ action: ADD_ACTION }}
                      >
                        <StyledAddButton>
                          <Plus size={24} />
                          <AddButtonText>{"Add"}</AddButtonText>
                        </StyledAddButton>
                      </Link>
                    )
                  : null}
              </AddAccountSection>
            </TableHeaderRightSection>
          </TableHeaderSection>

          <TableSection>
            <CustomDataTable
              columns={
                activeView === "Event"
                  ? EVENT_COLUMNS
                  : CONGRESSMAN_EVENT_COLUMNS
              }
              data={tableOption.data}
              noHeader
              progressPending={tableOption.isLoading}
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer
              paginationTotalRows={tableOption.totalRows}
              height="calc(100vh - 200px)"
            />
          </TableSection>
        </CardContainer>
      </CardSection>
      <CommentsModal
        isOpen={isModalShown}
        handleClose={toggleModal}
        comments={comments}
        fetchComments={fetchComments}
        congID={commentId}
        view="events"
        handleFetchTable={fetchDataTable}
      />
    </Wrapper>
  );
};

export default EventsList;
